import axios from "@/axios";
import { auth, getAppCheckHeader } from "@/main";

const endpoint = "/user";
const fullEndpoint = `${process.env.VUE_APP_BASEURL}/api${endpoint}`;

const formatDate = (date) => date?.split("T")[0] ?? "";

const LOAN_DOCUMENT_GROUPS = {
  incomeStatement: {
    category: "INCOME_STATEMENT",
    source: "LOAN_BEACON_FINANCIALS",
  },
  balanceSheet: {
    category: "BALANCE_SHEET",
    source: "LOAN_BEACON_FINANCIALS",
  },
  cashFlow: {
    category: "CASH_FLOW_STATEMENT",
    source: "LOAN_BEACON_FINANCIALS",
  },
  other: {
    category: "OTHER",
    source: "LOAN_BEACON_OTHER",
  },
};

const groupLoanDocuments = (documents = []) => {
  const grouped = {
    incomeStatement: [],
    balanceSheet: [],
    cashFlow: [],
    other: [],
  };

  documents.forEach((document) => {
    if (
      document.category === LOAN_DOCUMENT_GROUPS.incomeStatement.category &&
      document.source === LOAN_DOCUMENT_GROUPS.incomeStatement.source
    ) {
      grouped.incomeStatement.push(document);
      return;
    }

    if (
      document.category === LOAN_DOCUMENT_GROUPS.balanceSheet.category &&
      document.source === LOAN_DOCUMENT_GROUPS.balanceSheet.source
    ) {
      grouped.balanceSheet.push(document);
      return;
    }

    if (
      document.category === LOAN_DOCUMENT_GROUPS.cashFlow.category &&
      document.source === LOAN_DOCUMENT_GROUPS.cashFlow.source
    ) {
      grouped.cashFlow.push(document);
      return;
    }

    if (
      document.category === LOAN_DOCUMENT_GROUPS.other.category &&
      document.source === LOAN_DOCUMENT_GROUPS.other.source
    ) {
      grouped.other.push(document);
    }
  });

  return grouped;
};

const getFileTypeLabel = (document = {}, file = null) => {
  if (document.mime_type) {
    return document.mime_type.split("/")[1] || document.mime_type;
  }

  if (file?.type) {
    return file.type.split("/")[1] || file.type;
  }

  const filename = document.name || file?.name || "";
  return filename.includes(".") ? filename.split(".").pop() : "";
};

const mapLoanDocument = (document) => ({
  id: document.id,
  name: document.name || "",
  type: getFileTypeLabel(document),
  mimeType: document.mime_type || "",
  sizeBytes: document.size_bytes || 0,
  year: document.reporting_year ? String(document.reporting_year) : "",
  description: document.description || "",
  category: document.category || "",
  source: document.source || "",
  visibility: document.visibility || "",
  createdAt: document.createdAt || document.uploadedAt || null,
  updatedAt: document.updatedAt || null,
});

const User = {
  get: async () => {
    return axios
      .get(`${endpoint}`)
      .then((user) => user)
      .catch((error) => {
        console.log(error);
        return error;
      });
  },
  document: (file, type, callback) => {
    const controller = new AbortController();
    return {
      upload: async () => {
        const token = await auth.currentUser.getIdToken();
        await fetch(`${fullEndpoint}/document/${type}`, {
          method: "POST",
          signal: controller.signal,
          body: file,
          headers: {
            Authorization: `Bearer ${token}`,
            ...(await getAppCheckHeader())
          },
        })
          .then(async (response) => {
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              const progress = decoder.decode(value);
              let lines = progress.split("\n");
              for (const line of lines) {
                if (line.trim()) callback(line);
              }
            }
          })
          .catch((error) => {
            throw error;
          });
      },
      cancel: () => {
        console.log("Canceled!");
        controller.abort();
      },
    };
  },
  deleteDocument: async (id) => {
    return axios
      .delete(`${endpoint}/documents/${id}`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.log(error);
      });
  },
  getDocuments: async () => {
    return axios
      .get(`${endpoint}/documents`)
      .then((files) => {
        const documents = {
          usDrivingLicenseFront: null,
          usDrivingLicenseBack: null,
          usPassport: null,
        };
        files.forEach((file) => {
          documents[file.type] = file;
        });
        return documents;
      })
      .catch((error) => {
        console.log(error);
        return {
          usDrivingLicenseFront: null,
          usDrivingLicenseBack: null,
          usPassport: null,
        };
      });
  },
  business: {
    get: async () => {
      return axios
        .get(`${endpoint}/business`)
        .then((business) => ({
          id: business.id,
          name: business.name,
          description: business.description,
          street: business.street,
          street2: business.street2,
          city: business.city,
          state: business.state,
          zip: business.zip,
          phone: business.phone,
          email: business.email,
          entity: business.entity,
          ein: business.ein,
          ssn: business.ssn,
          dba: business.dba,
          established_date: formatDate(business.established_date),
          company_size: business.company_size ?? null,
          annual_revenue: business.annual_revenue ?? null,
          website: business.website,
          linkedin: business.linkedin,
          facebook: business.facebook,
          instagram: business.instagram,
          other_socials: business.other_socials,
          status: business.status,
          createdAt: business.createdAt,
          updatedAt: business.updatedAt,
          owners: business.owners,
          businessReports: business.businessReports,
          sbaData: business.sbaData,
          businessDocuments: business.businessDocuments,
          placesData: business.placesData,
          nearbyBusinessData: business.nearbyBusinessData,
          betaScores: business.betaScores,
          chatHistories: business.chatHistories,
        }))
        .catch((error) => {
          console.log(error);
          return null;
        });
    },
    save: async (payload) => {
      return axios
      .put(`${endpoint}/business`, payload)
      .then((business) => ({
        id: business.id,
        name: business.name,
        description: business.description,
        street: business.street,
        street2: business.street2,
        city: business.city,
        state: business.state,
        zip: business.zip,
        phone: business.phone,
        email: business.email,
        entity: business.entity,
        ein: business.ein,
        ssn: business.ssn,
        dba: business.dba,
        established_date: formatDate(business.established_date),
        website: business.website,
        linkedin: business.linkedin,
        facebook: business.facebook,
        instagram: business.instagram,
        other_socials: business.other_socials,
        status: business.status,
        createdAt: business.createdAt,
        updatedAt: business.updatedAt,
        owners: business.owners,
        businessReports: business.businessReports,
        sbaData: business.sbaData,
        businessDocuments: business.businessDocuments,
        placesData: business.placesData,
        nearbyBusinessData: business.nearbyBusinessData,
        betaScores: business.betaScores,
        chatHistories: business.chatHistories,
      }))
      .catch(({ error }) => {
        console.log(error);
        return { error };
      });
    },
    document: {
      instance: (file, callback) => {
        const controller = new AbortController();
        return {
          upload: async () => {
            const token = await auth.currentUser.getIdToken();
            await fetch(`${fullEndpoint}/business/document/verify-ownership`, {
              method: "POST",
              signal: controller.signal,
              body: file,
              headers: {
                Authorization: `Bearer ${token}`,
                ...(await getAppCheckHeader())
              },
            })
              .then(async (response) => {
                const reader = response.body.getReader();
                const decoder = new TextDecoder("utf-8");
                for (;;) {
                  const { done, value } = await reader.read();
                  if (done) break;
                  const progress = decoder.decode(value);
                  let lines = progress.split("\n");
                  for (const line of lines) {
                    if (line.trim()) callback(line);
                  }
                }
              })
              .catch((error) => {
                throw error;
              });
          },
          cancel: () => {
            console.log("Canceled!");
            controller.abort();
          },
        };
      },
      get: async () => {
        return axios
          .get(`${endpoint}/business/documents`)
          .then((files) => {
            console.log(files);
            return files[0];
          })
          .catch((error) => {
            console.log(error);
            return null;
          });
      },
      delete: async (id) => {
        return axios
          .delete(`${endpoint}/business/documents/${id}`)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            console.log(error);
          });
      },
    },
  },
  loanDocuments: {
    list: async () => {
      return axios
        .get(`${endpoint}/loan/documents`)
        .then((documents) => documents.map(mapLoanDocument))
        .catch((error) => {
          console.log(error);
          return [];
        });
    },

    grouped: async () => {
      return axios
        .get(`${endpoint}/loan/documents`)
        .then((documents) => {
          return {
            documents: groupLoanDocuments(documents.map(mapLoanDocument)),
            total: documents.length
          }
        })
        .catch((error) => {
          console.log(error);
          return {
            documents: {
              incomeStatement: [],
              balanceSheet: [],
              cashFlow: [],
              other: [],
            },
            total: 0
          };
        });
    },

    upload: async (type, file, payload = {}) => {
      const formData = new FormData();

      formData.append("file", file);

      if (payload.category) formData.append("category", payload.category);
      if (payload.source) formData.append("source", payload.source);
      if (payload.description) formData.append("description", payload.description);
      if (payload.year) formData.append("year", payload.year);
      if (payload.visibility) formData.append("visibility", payload.visibility);

      return axios
        .post(`${endpoint}/loan/document/${type}`, formData)
        .then((document) => ({
          id: document.id,
          name: document.name || file.name,
          type: getFileTypeLabel(document, file),
          mimeType: document.mime_type || file.type || "",
          sizeBytes: document.size_bytes || file.size || 0,
          year: payload.year ? String(payload.year) : "",
          description: payload.description || "",
          category: payload.category || "",
          source: payload.source || "",
          visibility: payload.visibility || "",
          createdAt: document.uploadedAt || document.createdAt || null,
          updatedAt: document.updatedAt || null,
        }))
        .catch((error) => {
          console.log(error);
          return null;
        });
    },

    update: async (id, payload) => {
      return axios
        .patch(`${endpoint}/business/documents/${id}`, payload)
        .then((document) => ({
          id: document.id,
          name: document.name || "",
          type: getFileTypeLabel(document),
          mimeType: document.mime_type || "",
          sizeBytes: document.size_bytes || 0,
          year: document.reporting_year ? String(document.reporting_year) : "",
          description: document.description || "",
          category: document.category || "",
          source: document.source || "",
          visibility: document.visibility || "",
          createdAt: document.createdAt || null,
          updatedAt: document.updatedAt || null,
        }))
        .catch((error) => {
          console.log(error);
          return null;
        });
    },

    delete: async (id) => {
      return axios
        .delete(`${endpoint}/business/documents/${id}`)
        .then((response) => response)
        .catch((error) => {
          console.log(error);
          return null;
        });
    },

    download: async (businessId, documentId) => {
      return axios
        .get(`${endpoint}/business/${businessId}/documents/${documentId}/download`, {
          responseType: "blob",
        })
        .then((file) => file)
        .catch((error) => {
          console.log(error);
          return null;
        });
    },
  },
  score: {
    get: async () => {
      return axios
        .get(`${endpoint}/score`)
        .then(({ betaScoreOutput }) => ({
          businessId: betaScoreOutput.businessId ?? null,
          score: {
            score: betaScoreOutput.betaScore ?? null,
            ratingLabel: betaScoreOutput.betaScoreRating ?? "",
            tier: betaScoreOutput.betaScoreTier ?? "",
            riskLevel: betaScoreOutput.betaScoreRiskLevel ?? "",
            bucket: betaScoreOutput.betaScoreColorBucket ?? null,
            dataConfidence: betaScoreOutput.betaScoreConfidence ?? "",
            date: new Date()
          },
          summary: betaScoreOutput.betaScoreSummary ?? "",
          factors: betaScoreOutput.featureDetails ?? []
        }))
        .catch(() => ({
          businessId: null,
          score: {
            score: null,
            ratingLabel: "",
            tier: "",
            bucket: null,
            dataConfidence: "",
            date: null
          },
          summary: "",
          factors: []
        }));
    },
  },
  chats: {
    get: async (businessId) => {
      return axios
        .get(`${endpoint}/business/${businessId}/chat-histories`)
        .then((chat) => chat)
        .catch((error) => {
          console.log(error);
          return [];
        });
    },
  },
  details: {
    get: async () => {
      return axios
        .get(`/user/user-details`)
        .then((details) => ({
          first_name: details.first_name,
          last_name: details.last_name || "",
          date_of_birth: formatDate(details.date_of_birth),
          identity_type: details.identity_type || "",
          personal_phone: details.personal_phone || "",
          personal_email: details.personal_email || "",
          address: details.address || "",
          address_2: details.address_2 || "",
          city: details.city || "",
          state: details.state || "",
          zip: details.zip || ""
        }))
        .catch((error) => {
          console.log(error);
          return {
            first_name: "",
            last_name: "",
            date_of_birth: "",
            identity_type: "",
            personal_phone: "",
            personal_email: "",
            address: "",
            address_2: "",
            city: "",
            state: "",
            zip: ""
          };
        });
    },
    post: async (payload) => {
      return axios
        .post(`/user/user-details`, payload)
        .then((details) => ({
          first_name: details.first_name || "",
          last_name: details.last_name || "",
          date_of_birth: formatDate(details.date_of_birth),
          identity_type: details.identity_type || "",
          personal_phone: details.personal_phone || "",
          personal_email: details.personal_email || "",
          address: details.address || "",
          address_2: details.address_2 || "",
          city: details.city || "",
          state: details.state || "",
          zip: details.zip || ""
        }))
        .catch((error) => {
          console.log(error);
          return {
            first_name: "",
            last_name: "",
            date_of_birth: "",
            identity_type: "",
            personal_phone: "",
            personal_email: "",
            address: "",
            address_2: "",
            city: "",
            state: "",
            zip: ""
          }
        });
    },
  },
  financials: {
    get: async (id) => {
      return axios
        .get(`/business/${id}/financials`)
        .then((response) => {
          const data = response.businessFinancials
          return {
            userId: data.userId,
            businessId: data.businessId,
            revenue: data.inputs.revenue,
            costOfGoodsSold: data.inputs.costOfGoodsSold,
            netIncome: data.inputs.netIncome,
            operatingIncome: data.inputs.operatingIncome,
            currentAssets: data.inputs.currentAssets,
            currentLiabilities: data.inputs.currentLiabilities,
            totalLiabilities: data.inputs.totalLiabilities,
            totalAssets: data.inputs.totalAssets,
            otherAssets: data.inputs.otherAssets,
            totalPersonalLiabilities: data.inputs.totalPersonalLiabilities,
            totalPersonalAssets: data.inputs.totalPersonalAssets,
            loanAmount: data.inputs.loanAmount,
            debtCoverage: {
              rawValue: data.kpis.debtCoverage.rawValue,
              score: data.kpis.debtCoverage.score,
              riskClass: data.kpis.debtCoverage.riskClass,
              riskDescription: data.kpis.debtCoverage.riskDescription
            },
            profitability: {
              rawValue: data.kpis.profitability.rawValue,
              score: data.kpis.profitability.score,
              riskClass: data.kpis.profitability.riskClass,
              riskDescription: data.kpis.profitability.riskDescription
            },
            liquidity: {
              rawValue: data.kpis.liquidity.rawValue,
              score: data.kpis.liquidity.score,
              riskClass: data.kpis.liquidity.riskClass,
              riskDescription: data.kpis.liquidity.riskDescription
            },
            leverage: {
              rawValue: data.kpis.leverage.rawValue,
              score: data.kpis.leverage.score,
              riskClass: data.kpis.leverage.riskClass,
              riskDescription: data.kpis.leverage.riskDescription
            },
            capitalAdequacy: {
              rawValue: data.kpis.capitalAdequacy.rawValue,
              score: data.kpis.capitalAdequacy.score,
              riskClass: data.kpis.capitalAdequacy.riskClass,
              riskDescription: data.kpis.capitalAdequacy.riskDescription
            },
            collateralCoverage: {
              rawValue: data.kpis.collateralCoverage.rawValue,
              score: data.kpis.collateralCoverage.score,
              riskClass: data.kpis.collateralCoverage.riskClass,
              riskDescription: data.kpis.collateralCoverage.riskDescription
            },
            guarantorStrength: {
              rawValue: data.kpis.guarantorStrength.rawValue,
              score: data.kpis.guarantorStrength.score,
              riskClass: data.kpis.guarantorStrength.riskClass,
              riskDescription: data.kpis.guarantorStrength.riskDescription
            },
            totalScore: data.kpis.totalScore,
            creditGrade: data.kpis.credit_grade,
            creditGradeRiskClass: data.kpis.creditGradeRiskClass
          };
        })
        .catch((error) => {
          console.log("Error fetching financials:", error);
          return {
            userId: "",
            businessId: "",
            revenue: "",
            costOfGoodsSold: "",
            netIncome: "",
            operatingIncome: "",
            currentAssets: "",
            currentLiabilities: "",
            totalLiabilities: "",
            totalAssets: "",
            otherAssets: "",
            totalPersonalLiabilities: "",
            totalPersonalAssets: "",
            loanAmount: "",
            debtCoverage: "",
            profitability: "",
            liquidity: "",
            leverage: "",
            capitalAdequacy: "",
            collateralCoverage: "",
            guarantorStrength: "",
            totalScore: "",
            creditGrade: "",
            creditGradeRiskClass: ""
          }
        });
    }
  }
};

export default User;
